import { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { initNotificationSound, playNotificationSound } from '../../services/notificationSound';

const WS_BASE_URL = (process.env.REACT_APP_BACKEND_URL || '')
  .replace(/\/+$/, '')
  .replace('https://', 'wss://')
  .replace('http://', 'ws://');

const RECONNECT_MS = 3000;
const PING_MS = 30000;

/**
 * Global notification-only WebSocket bridge.
 *
 * The backend fan-outs persisted notifications on the existing authenticated
 * chat WebSocket. Historically that socket existed only while the Messages
 * page was mounted, so users on dashboards/Admin pages could miss realtime
 * in-browser sound unless an FCM foreground push happened as well.
 *
 * This bridge stays mounted for every authenticated role and only consumes
 * notification/unread-count frames. Multiple sockets are supported by the
 * backend connection manager; notificationSound.js deduplicates the same
 * notification id when FCM + WS arrive together.
 */
export default function NotificationRealtimeBridge() {
  const { user, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !WS_BASE_URL) return undefined;

    initNotificationSound();
    stoppedRef.current = false;

    const clearTimers = () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pingRef.current) clearInterval(pingRef.current);
      reconnectRef.current = null;
      pingRef.current = null;
    };

    const connect = () => {
      const token = localStorage.getItem('snappipit_token');
      if (!token || stoppedRef.current) return;

      try {
        const ws = new WebSocket(`${WS_BASE_URL}/api/ws/chat/${user.id}?token=${token}`);
        wsRef.current = ws;

        ws.onopen = () => {
          if (pingRef.current) clearInterval(pingRef.current);
          pingRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, PING_MS);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'notification' && data.notification) {
              const n = data.notification;
              playNotificationSound(n.id || n.event_type);
              window.dispatchEvent(new CustomEvent('snappipit:notification', { detail: n }));
            } else if (data.type === 'unread_count_changed') {
              window.dispatchEvent(new CustomEvent('snappipit:notification_unread_changed', {
                detail: { category: data.category || 'general' },
              }));
            }
          } catch {
            // Ignore malformed/non-notification frames; chat pages own chat state.
          }
        };

        ws.onclose = (event) => {
          if (pingRef.current) clearInterval(pingRef.current);
          pingRef.current = null;
          if (wsRef.current === ws) wsRef.current = null;
          if (stoppedRef.current || event.code === 4001) return;
          reconnectRef.current = setTimeout(connect, RECONNECT_MS);
        };

        ws.onerror = () => {
          // onclose performs reconnection; keep this bridge silent in production.
        };
      } catch {
        if (!stoppedRef.current) reconnectRef.current = setTimeout(connect, RECONNECT_MS);
      }
    };

    connect();

    return () => {
      stoppedRef.current = true;
      clearTimers();
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        try { ws.close(); } catch { /* noop */ }
      }
    };
  }, [isAuthenticated, user?.id]);

  return null;
}
